/*
 * After the free event, without a plan (design W3 and pick 3A):
 *  - limited dashboard (`free_event.limited_access`, days 1–30 after the
 *    window): the event's leads, CSV export, plans; nothing else opens;
 *  - restore screen (`free_event.restore_only`, days 31–90): the gallery is
 *    offline, a plan brings it back; leads stay locked until then.
 * Both replace the full dashboard for the whole session. A path elsewhere
 * shows the "opens again when you pick a plan" note instead of the page.
 */
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  ClockIcon,
  ArrowPathIcon,
  LockClosedIcon,
  UserGroupIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";
import { t } from "copy/t";
import LeftMenu from "components/LeftMenu";
import { ft, fw, Btn, Msg, Alert, Photo } from "components/FreeEvent";
import { selectUser } from "containers/App/selectors";
import { signOut } from "containers/App/actions";
import FreeEventNotes from "containers/FreeEventNotes";
import useFreeEventRefresh from "containers/FreeEventBanner/useFreeEventRefresh";
import { getLeads, downloadLeads } from "utils/api/leads";
import { MARKETING_RENT_URL } from "utils/marketing";
import {
  formatEventDateTime,
  formatEventDays,
  formatEventStamp,
} from "utils/eventTime";
import {
  trackLimitedDashboardViewed,
  trackLeadsCsvDownloaded,
} from "utils/freeEventAnalytics";
import "containers/FreeStart/free-start.css";

const HELP_URL = "https://help.mvbth.com";
// Paths that are this screen itself; anything else gets the "opens again" note.
// `/plans` is the nav item that opens the plan sheet over the screen.
const HOME = ["/", "/leads", "/channels", "/plans"];
const selectUserState = selectUser();

const rowsOf = (res) => {
  const data = res && res.data;
  if (Array.isArray(data)) return data;
  return data && typeof data === "object" ? Object.values(data) : [];
};

export const nextLeadsPage = (last) => {
  const page = Number(last && last.current_page);
  const count = Number(last && last.page_count);
  return page && count && page < count ? page + 1 : undefined;
};

const fullName = (lead) =>
  [lead.first_name, lead.last_name].filter(Boolean).join(" ");

export const LeadsTable = ({ fe, token }) => {
  const tz = fe.event_time_zone;
  // Rails pages leads 100 at a time (`current_page` / `page_count`); a large
  // event gets "Show more" rather than a silently cut list.
  const leads = useInfiniteQuery(
    ["freeStart", "leads", fe.channel_id],
    ({ pageParam = 1 }) =>
      getLeads(
        token,
        `?source_channel=${fe.channel_id}&rows_per_page=100&page=${pageParam}`,
      ),
    {
      enabled: Boolean(fe.channel_id),
      retry: 1,
      getNextPageParam: nextLeadsPage,
    },
  );
  const rows = leads.data ? leads.data.pages.flatMap(rowsOf) : [];
  const days = fe.window_starts_at
    ? formatEventDays(fe.window_starts_at, fe.window_ends_at, tz)
    : null;

  let body;
  if (leads.isLoading && fe.channel_id) {
    body = [0, 1, 2, 3].map((i) => (
      <div className="lrow" key={i} aria-hidden="true">
        {[70, 80, 60, 50].map((w) => (
          <span
            key={w}
            className="skel"
            style={{ height: 14, width: `${w}%` }}
          />
        ))}
      </div>
    ));
  } else if (leads.isError) {
    body = (
      <div className="lempty">
        <ExclamationCircleIcon
          className="ic"
          aria-hidden="true"
          style={{ color: "var(--danger)" }}
        />
        <b>{ft("li.err")}</b>
        <p className="retry">
          <Btn className="sm ghost" onClick={() => leads.refetch()}>
            {ft("fb.retry")}
          </Btn>
        </p>
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <div className="lempty">
        <UserGroupIcon className="ic" aria-hidden="true" />
        <b>{fw("li.empty.b")}</b>
        <p>
          {fw("li.empty", {
            gallery_retained_until: formatEventDateTime(
              fe.gallery_retained_until,
              tz,
            ),
          })}
        </p>
      </div>
    );
  } else {
    body = (
      <>
        <div
          role="table"
          aria-label={
            fe.channel_name
              ? ft("li.lh", { channel_name: fe.channel_name })
              : ft("li.lh.none")
          }
        >
          <div className="lrow hd" role="row">
            {["li.col.name", "li.col.email", "li.col.phone", "li.col.date"].map(
              (k) => (
                <span key={k} role="columnheader">
                  {ft(k)}
                </span>
              ),
            )}
          </div>
          {rows.map((lead) => (
            <div
              className="lrow"
              role="row"
              key={lead.id || `${lead.email}-${lead.created_at}`}
            >
              <span className="n" role="cell">
                {fullName(lead)}
              </span>
              <span className="e" role="cell">
                {lead.email}
              </span>
              <span className="p" role="cell">
                {lead.phone}
              </span>
              <span className="d" role="cell">
                {/* time_shared is the guest's own share time; created_at is when
                    the server wrote the row, later for a queued upload. */}
                {lead.time_shared || lead.created_at
                  ? formatEventStamp(lead.time_shared || lead.created_at, tz)
                  : ""}
              </span>
            </div>
          ))}
        </div>
        {leads.hasNextPage && (
          <p className="more">
            <Btn
              className="sm ghost"
              busy={leads.isFetchingNextPage}
              busyLabel={ft("li.more")}
              onClick={() => leads.fetchNextPage()}
            >
              {ft("li.more")}
            </Btn>
          </p>
        )}
      </>
    );
  }

  return (
    <section className="leads" aria-busy={leads.isLoading || undefined}>
      <div className="lh">
        <h2>
          {fe.channel_name
            ? ft("li.lh", { channel_name: fe.channel_name })
            : ft("li.lh.none")}
        </h2>
        {days && (
          <span>
            {ft("li.lh.d", { event_dates: days.dates, time_zone: days.zone })}
          </span>
        )}
      </div>
      {body}
    </section>
  );
};

export const LimitedView = ({ fe, token, restore, offPath, openPlans }) => {
  const [csv, setCsv] = useState("idle");
  const tz = fe.event_time_zone;
  const vars = {
    gallery_retained_until: formatEventDateTime(fe.gallery_retained_until, tz),
    gallery_restorable_until: formatEventDateTime(
      fe.gallery_restorable_until,
      tz,
    ),
  };

  const runCsv = async () => {
    if (csv === "busy") return;
    setCsv("busy");
    try {
      await downloadLeads(
        token,
        fe.channel_id ? `?source_channel=${fe.channel_id}` : "",
      );
      trackLeadsCsvDownloaded();
      setCsv("ok");
    } catch (_e) {
      setCsv("err");
    }
  };

  const prints = restore
    ? ["guest-solo-b", "guest-solo-c", "guest-solo-d"]
    : ["guest-solo-a", "guest-solo-d", "guest-group"];

  return (
    <div className="mb-fs lim mb-fs-col">
      {offPath && (
        <Alert kind="info" icon={LockClosedIcon} title={ft("li.redir")} />
      )}
      <section className="lim-hero">
        <div>
          <span className="chip">
            {restore ? (
              <ArrowPathIcon className="ic" aria-hidden="true" />
            ) : (
              <ClockIcon className="ic" aria-hidden="true" />
            )}
            {ft(restore ? "rs.chip" : "li.chip", vars)}
          </span>
          <h1>{fw(restore ? "rs.h" : "li.h")}</h1>
          <p>{fw(restore ? "rs.p" : "li.p", vars)}</p>
          <div className="lim-act">
            {restore ? (
              <Btn className="sm" onClick={openPlans}>
                {ft("fb.plans")}
              </Btn>
            ) : (
              <>
                <Btn
                  className="sm"
                  busy={csv === "busy"}
                  busyLabel={ft("li.busy")}
                  onClick={runCsv}
                >
                  {ft(csv === "err" ? "fb.retry" : "fb.csv")}
                </Btn>
                <Btn className="sm ghost" onClick={openPlans}>
                  {ft("fb.plans")}
                </Btn>
              </>
            )}
            <a className="solo" href={MARKETING_RENT_URL}>
              {ft("li.rent")}
            </a>
          </div>
          {csv === "err" && <Msg>{ft("fb.csv.err")}</Msg>}
          {csv === "ok" && (
            <Msg kind="ok">{t("toast.leads.csvExportStarted")}</Msg>
          )}
        </div>
        <div
          className={`lim-prints${restore ? " dim" : ""}`}
          aria-hidden="true"
        >
          {prints.map((p) => (
            <Photo key={p} slot={p} />
          ))}
        </div>
      </section>
      {restore ? (
        <section className="leads">
          <div className="lempty">
            <LockClosedIcon className="ic" aria-hidden="true" />
            <b>{fw("rs.locked")}</b>
            <p>{fw("li.redir")}</p>
          </div>
        </section>
      ) : (
        <LeadsTable fe={fe} token={token} />
      )}
    </div>
  );
};

// Which shell, if any, the payload asks for.
export const limitedMode = (fe) => {
  if (!fe || fe.paid_at) return null;
  if (fe.restore_only) return "restore";
  if (fe.limited_access) return "limited";
  return null;
};

const FreeEventLimited = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const openedFor = React.useRef(false);
  const user = useSelector(selectUserState);
  const fe = user && user.free_event;
  const mode = limitedMode(fe);
  // This shell wraps every signed-in page, so the one refresh here keeps both
  // the banner (full app) and this screen current.
  useFreeEventRefresh(fe);

  React.useEffect(() => {
    if (mode) trackLimitedDashboardViewed();
  }, [mode]);

  // The Plans nav item lands on /plans and opens the sheet once per visit.
  const onPlans = Boolean(mode) && location.pathname === "/plans";
  React.useEffect(() => {
    if (!onPlans) {
      openedFor.current = false;
      return;
    }
    if (openedFor.current || params.get("plans") === "open") return;
    openedFor.current = true;
    setParams(
      (p) => {
        p.set("plans", "open");
        return p;
      },
      { replace: true },
    );
  }, [onPlans, params, setParams]);

  if (!mode) return children;

  const openPlans = () =>
    setParams((p) => {
      p.set("plans", "open");
      return p;
    });
  const items = [
    ...(mode === "limited"
      ? [{ resource: "leads", name: ft("nav.leads"), iconName: "leads" }]
      : []),
    { resource: "plans", name: ft("li.plans"), iconName: "account" },
    { href: HELP_URL, name: ft("nav.help"), iconName: "help" },
  ];
  const offPath = !HOME.includes(location.pathname.replace(/\/+$/, "") || "/");

  return (
    <LeftMenu items={items} signOut={() => dispatch(signOut())}>
      {/* Back from checkout, the notes keep polling the session here too: its
          paid_at is what unlocks this shell into the full app. */}
      <div className="mb-fs-col">
        <FreeEventNotes />
      </div>
      <LimitedView
        fe={fe}
        token={user.authToken}
        restore={mode === "restore"}
        offPath={offPath}
        openPlans={openPlans}
      />
    </LeftMenu>
  );
};

export default FreeEventLimited;
